import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: BACKEND_URL ? `${BACKEND_URL}/api` : '/api',
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = localStorage.getItem('tenant_id');
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant_id');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
};

export const tenantsAPI = {
  list: () => api.get('/tenants'),
  get: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  remove: (id) => api.delete(`/tenants/${id}`),
  superList: () => api.get('/super/tenants'),
  superCreate: (data) => api.post('/super/tenants', data),
  superUpdate: (id, data) => api.put(`/super/tenants/${id}`, data),
  superRemove: (id) => api.delete(`/super/tenants/${id}`),
};

export const usersAPI = {
  list: () => api.get('/users'),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  remove: (id) => api.delete(`/users/${id}`),
};

export const busesAPI = {
  list: () => api.get('/buses'),
  get: (id) => api.get(`/buses/${id}`),
  create: (data) => api.post('/buses', data),
  update: (id, data) => api.put(`/buses/${id}`, data),
  remove: (id) => api.delete(`/buses/${id}`),
};

export const routesAPI = {
  list: () => api.get('/routes'),
  get: (id) => api.get(`/routes/${id}`),
  create: (data) => api.post('/routes', data),
  update: (id, data) => api.put(`/routes/${id}`, data),
  remove: (id) => api.delete(`/routes/${id}`),
};

export const studentsAPI = {
  list: () => api.get('/students'),
  get: (id) => api.get(`/students/${id}`),
  listByRoute: (routeId) => api.get(`/students/route/${routeId}`),
  create: (data) => {
    const config = data instanceof FormData 
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    return api.post('/students', data, config);
  },
  update: (id, data) => {
    const config = data instanceof FormData 
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    return api.put(`/students/${id}`, data, config);
  },
  remove: (id) => api.delete(`/students/${id}`),
  listLocations: (studentId) => api.get(`/students/${studentId}/locations`),
  addLocation: (studentId, data) => api.post(`/students/${studentId}/locations`, data),
  updateLocation: (studentId, locationId, data) => api.put(`/students/${studentId}/locations/${locationId}`, data),
  removeLocation: (studentId, locationId) => api.delete(`/students/${studentId}/locations/${locationId}`),
};

export const tripSessionsAPI = {
  list: (params) => api.get('/trip-sessions', { params }),
  active: () => api.get('/trip-sessions/active'),
  get: (id) => api.get(`/trip-sessions/${id}`),
  create: (data) => api.post('/trip-sessions', data),
  end: (id) => api.put(`/trip-sessions/${id}/end`),
  cancel: (id) => api.put(`/trip-sessions/${id}/cancel`),
  remove: (id) => api.delete(`/trip-sessions/${id}`),
  getGps: (id) => api.get(`/trip-sessions/${id}/gps`),
  getAttendance: (id) => api.get(`/trip-sessions/${id}/attendance`),
};

export const attendanceAPI = {
  list: () => api.get('/attendance'),
  listByTrip: (tripId) => api.get(`/attendance/trip/${tripId}`),
  create: (data) => api.post('/attendance', data),
};

export const absencesAPI = {
  list: () => api.get('/absences'),
  create: (data) => api.post('/absences', data),
  remove: (id) => api.delete(`/absences/${id}`),
};

export const setupAPI = {
  status: () => api.get('/setup/status'),
  testConnection: (data) => api.post('/setup/test-connection', data),
  saveConfig: (data) => api.post('/setup/save-config', data),
  init: () => api.post('/setup/init'),
  migrate: () => api.post('/setup/migrate'),
  reboot: () => api.post('/setup/reboot'),
};

export default api;
